<template>
  <div v-if="$auth.check()">
    <loader v-if="initialLoading"/>
    <v-container class="pa-0" grid-list-md v-else>
      <v-row>
        <v-col :cols="12" class="d-flex justify-space-between">
          <h1>{{ $t('texts.subscriptionInformation') }}</h1>
          <div>
            <v-btn
              icon
              @click="infoDialog.open = true"
            >
              <v-icon>info</v-icon>
            </v-btn>
            <v-btn
              color="primary"
              @click="showAddSubscriptionDialog()"
            >
              {{ $t('texts.addSubscription') }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col :cols="12">
          <subscription
            :subscriptions="subscriptions"
          />
        </v-col>
      </v-row>
      <v-row v-if="subscriptions.length === 0">
        <v-col :cols="12">
          <v-card>
            <v-card-text>
              {{ $t('texts.jobAdvertisementsOnlyWithValidSubscription') }}
            </v-card-text>
            <v-card-actions>
              <v-btn
                text
                :to="{ name: 'page.job-advertisement', hash: '#section-3', params: { lang: language } }"
              >
                {{ $t('texts.moreInformation') }}
              </v-btn>
              <v-btn
                color="primary"
                @click="showAddSubscriptionDialog()"
              >
                {{ $t('texts.addSubscription') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="invoices.length > 0">
        <v-col :cols="12">
          <v-card class="mt-3">
            <v-card-title class="primary white--text">
              <span class="title">
                {{ $tc('texts.receipt', 2) }}
              </span>
            </v-card-title>
            <v-card-text class="pa-2">
              <v-data-table
                :headers="headers"
                :items="formattedItems"
                must-sort
              >
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon class="mx-0"
                    :disabled="loadingInvoiceId !== null"
                    :loading="loadingInvoiceId === item.invoice_id"
                    @click="showPdfDialog({
                      id: item.invoice_id,
                      company_id: item.company_id,
                      title: `${$t('texts.invoice.title')} ${item.company}`
                    })"
                  >
                    <v-icon color="primary">insert_drive_file</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <add-subscription-dialog
        :products="productsBase"
      />
      <add-additional-company-dialog
        :products="productsAdditional"
      />
      <info-dialog
        :data="infoDialog"
      >
        <template slot="title">
          {{ $t('texts.subscriptionInfoDialog.label') }}
        </template>
        <template slot="content">
          <i18n path="texts.subscriptionInfoDialog.text">
            <router-link
              :to="{ name: 'page.job-advertisement', hash: '#section-3', params: { lang: language } }"
            >
              {{ $t('texts.subscriptionInfoDialog.link') }}
            </router-link>
          </i18n>
        </template>
      </info-dialog>
      <edit-credit-card-dialog/>
      <change-subscription-dialog
        :products="productsBase"
      />
      <cancel-subscription-dialog/>
      <add-coupon-dialog/>
    </v-container>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import InfoDialog from '@/components/_organisms/InfoDialog.vue';
import Subscription from './item/Subscription.vue';
import AddSubscriptionDialog from './dialogs/AddSubscriptionDialog.vue';
import AddAdditionalCompanyDialog from './dialogs/AddAdditionalCompanyDialog.vue';
import EditCreditCardDialog from './dialogs/EditCreditCardDialog.vue';
import ChangeSubscriptionDialog from './dialogs/ChangeSubscriptionDialog.vue';
import AddCouponDialog from './dialogs/AddCouponDialog.vue';
import CancelSubscriptionDialog from './dialogs/CancelSubscriptionDialog.vue';

const { mapState: mapUserState } = createNamespacedHelpers('user');
const { mapState: mapSubscriptionState, mapActions: mapSubscriptionActions } = createNamespacedHelpers('subscription');

export default {
  name: 'SubscriptionAdmin',
  components: {
    Subscription,
    AddSubscriptionDialog,
    AddAdditionalCompanyDialog,
    InfoDialog,
    EditCreditCardDialog,
    ChangeSubscriptionDialog,
    AddCouponDialog,
    CancelSubscriptionDialog,
  },
  data() {
    return {
      infoDialog: {
        open: false,
      },
      loadingInvoiceId: null,
      pagination: {
        sortBy: 'date',
        descending: true,
      },
    };
  },
  created() {
    this.fetchInitial();
  },
  computed: {
    ...mapUserState([
      'language',
    ]),
    ...mapSubscriptionState([
      'initialLoading',
      'productsBase',
      'productsAdditional',
      'subscriptions',
      'invoices',
      'fetchSubscriptionPDFPending',
    ]),
    headers() {
      const headers = [
        {
          text: this.$i18n.t('texts.name'),
          value: 'company',
        },
        {
          text: this.$i18n.t('texts.date'),
          value: 'formattedDate',
        },
        {
          text: this.$i18n.t('texts.amount'),
          value: 'formattedAmount',
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'right',
        },
      ];
      return headers;
    },
    filteredInvoices() {
      return this.invoices.filter((invoice) => invoice.amount !== '0.00');
    },
    formattedItems() {
      if (this.filteredInvoices.length > 0) {
        return this.filteredInvoices.map((item) => {
          const formattedDate = this.$options.filters.formatDateShort(item.date);
          const formattedAmount = `CHF ${item.amount}`;
          const formattedItem = {
            ...item,
            formattedDate,
            formattedAmount,
          };
          return formattedItem;
        });
      }
      return [];
    },
  },
  methods: {
    ...mapSubscriptionActions([
      'fetchInitial',
      'fetchSubscriptionPDF',
    ]),
    showAddSubscriptionDialog() {
      this.$eventBus.$emit('show-add-subscription-dialog');
    },
    showPdfDialog(receipt) {
      this.loadingInvoiceId = receipt.id;
      this.fetchSubscriptionPDF(receipt).then((response) => {
        receipt.pdfLink = response.data;
        this.loadingInvoiceId = null;
        this.$eventBus.$emit('show-pdf-dialog', receipt);
      }).catch((error) => {
        console.log(error, 'error');
      });
    },
  },
};
</script>
